import { FC, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useGetUser from '../../../main/hooks/useGetUser';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import { resolvePageRedirectRoute } from '../../../main/constants/pageRedirect';
import axiosInstance from '../../../main/initializers/axios';
import AuthManager from '../../../main/utils/authManager';
import { setMenu, invalidateMenu } from '../../../main/store/stores/user/menu.store';
import { setPermission, invalidatePermissions } from '../../../main/store/stores/user/permission.store';
import { setUser } from '../../../main/store/stores/user/user.store';
import AuthSplitLayout from '../shared/AuthSplitLayout';
import '../register/Register.css';

const fieldVariants = {
  hidden: {
    opacity: 0,
    y: 16,
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.1 + i * 0.06,
      type: "spring" as const,
      stiffness: 100,
      damping: 20,
    },
  }),
} satisfies Variants;

const Login: FC = () => {
  const [searchParams] = useSearchParams();
  const [email, setemail] = useState<string | undefined | null>(null);
  const [password, setPassword] = useState<string | undefined | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState<'request' | 'reset'>('request');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');
  const { i18n, t } = useTranslation();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useGetUser();
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    const queryEmail = searchParams.get('email');
    if (queryEmail) {
      setemail(queryEmail);
    }
  }, [searchParams]);

  useEffect(() => {
    setForgotEmail((email ?? searchParams.get('email') ?? '').trim().toLowerCase());
  }, [email, searchParams]);

  useEffect(() => {
    if (!user || hasRedirectedRef.current) {
      return;
    }

    hasRedirectedRef.current = true;
    navigate(resolvePageRedirectRoute((user as any)?.pageRedirect), { replace: true });
  }, [navigate, user]);

  const handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    const normalizedEmail = email?.trim().toLowerCase() ?? '';
    const normalizedPassword = password?.trim() ?? '';

    if (!normalizedEmail || !normalizedPassword) {
      setLoginError('Email and password are required.');
      return;
    }

    try {
      setIsSubmitting(true);
      setLoginError('');

      const response = await AuthManager.loginWithCredentials({ email: normalizedEmail, password: normalizedPassword });
      if (response.redirectTo) {
        dispatch(invalidateMenu());
        dispatch(invalidatePermissions());
        navigate(`${response.redirectTo}${response.redirectEmail ? `?email=${encodeURIComponent(response.redirectEmail)}` : ''}`, { replace: true });
        return;
      }

      dispatch(setPermission(response.permissions));
      dispatch(setMenu(response.menu));
      dispatch(setUser(response.user));
      navigate(resolvePageRedirectRoute((response.user as any)?.pageRedirect), { replace: true });
    } catch (err: any) {
      const responseData = err?.response?.data;
      setLoginError(responseData?.message || responseData || 'Login failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openForgotPassword = () => {
    setForgotOpen(true);
    setForgotStep('request');
    setForgotOtp('');
    setForgotNewPassword('');
    setForgotConfirmPassword('');
    setForgotMessage('');
    setForgotError('');
    setForgotEmail((email ?? searchParams.get('email') ?? '').trim().toLowerCase());
  };

  const closeForgotPassword = () => {
    if (forgotLoading) {
      return;
    }

    setForgotOpen(false);
    setForgotStep('request');
    setForgotOtp('');
    setForgotNewPassword('');
    setForgotConfirmPassword('');
    setForgotMessage('');
    setForgotError('');
  };

  const handleForgotRequest = async () => {
    const normalizedEmail = forgotEmail.trim().toLowerCase();
    if (!normalizedEmail) {
      setForgotError('Email is required.');
      setForgotMessage('');
      return;
    }

    try {
      setForgotLoading(true);
      setForgotError('');
      setForgotMessage('');
      await axiosInstance.post(ApiConstants.publicUser.forgotPassword, { email: normalizedEmail });
      setForgotStep('reset');
      setForgotMessage('Verification code sent to your email.');
    } catch (err: any) {
      setForgotError(err?.response?.data?.message || err?.response?.data || 'Unable to send verification code.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotReset = async () => {
    const normalizedEmail = forgotEmail.trim().toLowerCase();
    if (!normalizedEmail || !forgotOtp.trim() || !forgotNewPassword.trim() || !forgotConfirmPassword.trim()) {
      setForgotError('Email, code and new password are required.');
      setForgotMessage('');
      return;
    }

    try {
      setForgotLoading(true);
      setForgotError('');
      setForgotMessage('');
      await axiosInstance.post(ApiConstants.publicUser.resetPassword, {
        email: normalizedEmail,
        otpCode: forgotOtp.trim(),
        newPassword: forgotNewPassword,
        confirmPassword: forgotConfirmPassword,
      });

      setForgotMessage('Password changed successfully. You can login now.');
      setPassword(forgotNewPassword);
      setemail(normalizedEmail);
      window.setTimeout(() => closeForgotPassword(), 800);
    } catch (err: any) {
      setForgotError(err?.response?.data?.message || err?.response?.data || 'Password reset failed.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <AuthSplitLayout
      mode="login"
      footer={
        <>
          {i18n.t('login.footer')}{' '}
          <a href="#">{i18n.t('login.privacyPolicy')}</a>
          {' / '}
          <a href="#">{i18n.t('login.termsOfService')}</a>
        </>
      }
    >
      {/* Badge */}
      <motion.div
        className="registerBadge"
        variants={{
          hidden: { opacity: 0, y: -10 },
          visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100, damping: 18 } },
        }}
        initial="hidden"
        animate="visible"
      >
        <div className="registerAvatarWrap">
          <svg className="registerAvatarIcon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <div>
          <p className="registerEyebrow">{i18n.t('login.badgeSubtitle')}</p>
          <h1 className="registerTitle">{i18n.t('login.welcomeTitle')}</h1>
          <p className="registerSubtitle">{i18n.t('login.welcomeSubtitle')}</p>
        </div>
      </motion.div>

      {/* Form */}
      <form className="registerForm" noValidate onSubmit={handleSubmit}>
        <motion.div custom={0} variants={fieldVariants} initial="hidden" animate="visible">
          <label className="authFieldLabel">{i18n.t('generic.email')}</label>
          <input
            className="authInput"
            type="email"
            required
            value={email ?? ''}
            onChange={(event) => setemail(event.target.value)}
            autoComplete="email"
            placeholder="you@example.com"
          />
        </motion.div>

        <motion.div custom={1} variants={fieldVariants} initial="hidden" animate="visible">
          <label className="authFieldLabel">{i18n.t('generic.password')}</label>
          <input
            className="authInput"
            type="password"
            required
            value={password ?? ''}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            placeholder="••••••••"
          />
        </motion.div>

        {/* Error */}
        <AnimatePresence>
          {loginError && (
            <motion.div
              className="registerError"
              initial={{ opacity: 0, height: 0, marginBottom: 0 }}
              animate={{ opacity: 1, height: 'auto', marginBottom: 0 }}
              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
              transition={{ duration: 0.2 }}
            >
              {loginError}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Remember + Forgot */}
        <motion.div
          className="authRow"
          custom={2}
          variants={fieldVariants}
          initial="hidden"
          animate="visible"
        >
          <label className="authCheckLabel">
            <input type="checkbox" className="ui-check" />
            <span>{i18n.t('login.rememberMe')}</span>
          </label>
          <button type="button" className="authLinkBtn" onClick={openForgotPassword}>
            {i18n.t('login.forgot', { defaultValue: 'Forgot password?' })}
          </button>
        </motion.div>

        {/* Submit */}
        <motion.div custom={3} variants={fieldVariants} initial="hidden" animate="visible">
          <button
            type="submit"
            className="authBtn authBtn--primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="authBtnLoader" />
            ) : (
              <>
                {i18n.t('login.signInButton')}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </>
            )}
          </button>
        </motion.div>

        {/* Action buttons */}
        <motion.div
          className="authBtnGroup"
          custom={4}
          variants={fieldVariants}
          initial="hidden"
          animate="visible"
        >
          <button type="button" className="authBtn authBtn--outline" onClick={() => navigate('/register')}>
            {i18n.t('login.createAccount')}
          </button>
          <button
            type="button"
            className="authBtn authBtn--outline"
            onClick={() => navigate(`/activate-account${email?.trim() ? `?email=${encodeURIComponent(email.trim().toLowerCase())}` : ''}`)}
          >
            Activate
          </button>
          <button type="button" className="authBtn authBtn--outline" onClick={() => navigate('/')}>
            {i18n.t('login.viewDemo')}
          </button>
        </motion.div>
      </form>

      {/* Forgot Password Modal */}
      <AnimatePresence>
        {forgotOpen && (
          <motion.div
            className="authModalOverlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeForgotPassword}
          >
            <motion.div
              className="authModal"
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ type: 'spring', stiffness: 200, damping: 25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="authModalTitle">
                {t('login.forgot', { defaultValue: 'Forgot password?' })}
              </h2>
              <p className="authModalDesc">
                {forgotStep === 'request'
                  ? 'Enter your booking account email and we will send you a verification code.'
                  : 'Enter the verification code from your email and choose a new password.'}
              </p>

              <div className="authModalFields">
                <label className="authFieldLabel">{t('generic.email')}</label>
                <input
                  className="authInput"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com"
                />

                {forgotStep === 'reset' && (
                  <motion.div
                    className="authModalFields"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                  >
                    <label className="authFieldLabel">OTP</label>
                    <input
                      className="authInput"
                      value={forgotOtp}
                      onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                    />

                    <label className="authFieldLabel">{t('generic.newPassword', { defaultValue: 'New password' })}</label>
                    <input
                      className="authInput"
                      type="password"
                      value={forgotNewPassword}
                      onChange={(e) => setForgotNewPassword(e.target.value)}
                      placeholder="••••••••"
                    />

                    <label className="authFieldLabel">{t('generic.confirmPassword', { defaultValue: 'Confirm password' })}</label>
                    <input
                      className="authInput"
                      type="password"
                      value={forgotConfirmPassword}
                      onChange={(e) => setForgotConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                  </motion.div>
                )}
              </div>

              <AnimatePresence>
                {forgotError && (
                  <motion.div
                    className="registerError"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    {forgotError}
                  </motion.div>
                )}
                {forgotMessage && (
                  <motion.div
                    className="registerError registerSuccess"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    {forgotMessage}
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="authModalActions">
                <button
                  type="button"
                  className="authBtn authBtn--outline"
                  onClick={closeForgotPassword}
                  disabled={forgotLoading}
                >
                  {t('generic.cancel')}
                </button>
                <button
                  type="button"
                  className="authBtn authBtn--primary"
                  onClick={forgotStep === 'request' ? handleForgotRequest : handleForgotReset}
                  disabled={forgotLoading}
                >
                  {forgotLoading ? (
                    <span className="authBtnLoader" />
                  ) : forgotStep === 'request' ? (
                    'Send code'
                  ) : (
                    'Reset password'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AuthSplitLayout>
  );
};

export default Login;
